/**
 * Sprint 82 — Centre de notifications in-app.
 * Sprint 83 — Bridge Reconnect Wizard déclenché depuis notification bridge.broken.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Bell, AlertTriangle, AlertCircle, CheckCircle2, Trash2, Check, X,
  Landmark, ArrowRight, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const SEV_META = {
  info: { color: "bg-blue-100 text-blue-800", Icon: Bell },
  warning: { color: "bg-amber-100 text-amber-800", Icon: AlertTriangle },
  critical: { color: "bg-rose-100 text-rose-800", Icon: AlertCircle },
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [wizardBankId, setWizardBankId] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/notifications")
    .then((r) => setItems(r.data.notifications))
    .catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const markAllRead = async () => {
    try {
      const r = await api.post("/notifications/mark-all-read");
      toast.success(`${r.data.marked} notification(s) marquée(s) lues`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/notifications/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openAction = (n) => {
    if (n.kind === "bridge.broken") {
      setWizardBankId(n.metadata?.item_id || "unknown");
    } else if (n.action_url) {
      navigate(n.action_url);
    }
    if (!n.read) markRead(n.id);
  };

  const unread = items.filter((n) => !n.read);
  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <Bell className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Notifications</h1>
            <p className="text-sm text-slate-500">
              {unread.length} non lue(s) · Alertes Bridge, SEPA, exports.
            </p>
          </div>
        </div>
        {unread.length > 0 && (
          <Button variant="outline" onClick={markAllRead}
                   data-testid="mark-all-read-btn">
            <Check className="w-4 h-4 mr-1" />Tout marquer lu
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
              Aucune notification. Tout est sous contrôle 👍
            </div>
          ) : (
            <div className="divide-y">
              {items.map((n) => {
                const { color, Icon } = SEV_META[n.severity] || SEV_META.info;
                return (
                  <div key={n.id}
                        className={`p-3 flex items-start gap-3 hover:bg-slate-50 ${!n.read ? "bg-blue-50/30" : ""}`}
                        data-testid={`notif-${n.id}`}>
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm ${!n.read ? "font-semibold" : "font-medium"}`}>
                          {n.title}
                        </p>
                        {!n.read && <Badge className="bg-blue-600 text-white text-[10px]">NOUVEAU</Badge>}
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5">{n.body}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(n.created_at).toLocaleString("fr-FR")}
                      </p>
                      {n.action_label && (
                        <Button size="sm" variant="link" className="px-0 mt-1 h-auto"
                                onClick={() => openAction(n)}
                                data-testid={`action-${n.id}`}>
                          {n.action_label} <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {!n.read && (
                        <Button variant="ghost" size="sm" onClick={() => markRead(n.id)}
                                 title="Marquer lu">
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => remove(n.id)}
                               title="Supprimer">
                        <Trash2 className="w-4 h-4 text-slate-400" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {wizardBankId && (
        <BridgeReconnectWizard itemId={wizardBankId}
                                onClose={() => setWizardBankId(null)} />
      )}
    </div>
  );
}

// =========================================================================
// SPRINT 83 — Bridge Reconnect Wizard (3 étapes guidées)
// =========================================================================
function BridgeReconnectWizard({ itemId, onClose }) {
  const [step, setStep] = useState(1);
  const [connectUrl, setConnectUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const startReconnect = async () => {
    setLoading(true);
    try {
      const r = await api.post("/bridge/reconnect-session",
                                 { item_id: itemId });
      setConnectUrl(r.data.redirect_url || r.data.url);
      setStep(2);
    } catch (e) {
      toast.error("Impossible de démarrer la reconnexion. Contactez le support.");
    } finally { setLoading(false); }
  };

  const verify = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/bridge/verify-item/${itemId}`);
      if (r.data.status === 0 || r.data.status === 1100) {
        toast.success("✓ Connexion rétablie");
        onClose();
      } else {
        toast.warning(`Statut Bridge : ${r.data.status} — encore en attente`);
        setStep(3);
      }
    } catch (e) {
      toast.error("Vérification échouée");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-blue-600" />
            Reconnecter la banque — Étape {step}/3
          </DialogTitle>
          <DialogDescription>
            Rétablissez votre connexion Bridge en 30 secondes.
          </DialogDescription>
        </DialogHeader>
        {step === 1 && (
          <div className="space-y-3 py-3">
            <p className="text-sm">Nous allons vous rediriger vers votre banque via Bridge (agrégateur PSD2).</p>
            <ol className="text-sm text-slate-600 space-y-1 list-decimal pl-5">
              <li>Confirmez votre identité côté banque (SCA)</li>
              <li>Autorisez à nouveau la lecture des comptes</li>
              <li>Revenez ici pour vérifier</li>
            </ol>
            <Button onClick={startReconnect} disabled={loading} className="w-full"
                     data-testid="start-reconnect-btn">
              {loading ? "…" : "Commencer la reconnexion"}
            </Button>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-3 py-3">
            <p className="text-sm">Ouvrez le portail de reconnexion :</p>
            {connectUrl ? (
              <a href={connectUrl} target="_blank" rel="noopener noreferrer"
                 className="block bg-blue-600 hover:bg-blue-700 text-white text-center py-2 rounded"
                 data-testid="open-bridge-btn">
                <ExternalLink className="w-4 h-4 inline mr-2" />
                Ouvrir le portail Bridge
              </a>
            ) : <p className="text-sm text-slate-500">Chargement…</p>}
            <p className="text-xs text-slate-500">Après confirmation côté banque, revenez et cliquez sur Vérifier.</p>
            <Button onClick={verify} disabled={loading} className="w-full"
                     variant="outline" data-testid="verify-reconnect-btn">
              {loading ? "Vérification…" : "J'ai terminé — Vérifier"}
            </Button>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-3 py-3">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
              La reconnexion n'est pas encore effective. Bridge peut prendre jusqu'à
              2 minutes pour actualiser le statut.
            </div>
            <Button onClick={verify} disabled={loading} className="w-full">
              Re-vérifier
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-1" />Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
